// The BASE names of a user's local databases, written in one place (df-fc67f2accfb9).
//
// Several places must agree on these names character for character. The #6448 start-up
// delete keeps a leftover impersonation copy only when its name matches a name this start
// opens, so a copy of the name that drifted from the others would make the next start
// delete a database it should keep.
//
// These are BASE names. A `DatabaseWrapper` opens its own database under
// `currentLocalStoreName(storage, <base name>)`, which adds a generation suffix
// (`__g1`) on a device that went through a #4401 local-store reset. An impersonation copy
// is opened under the plain base name. Each caller applies exactly the mapping it needs.

import { currentLocalStoreName, GenerationStorage } from './local-store-generation';

/** The user's own local database, and the name an impersonation copy of that user uses. */
export function localUserDbBaseName(uid: string): string {
  return 'userdb-fr_' + uid;
}

/** A shared database's local copy for this user, such as `global_common`. */
export function localSharedDbBaseName(databaseID: string, uid: string): string {
  return databaseID + '-fr_' + uid;
}

/** The local copy of a client's database that a coach opens while managing that client. */
export function localCoachClientDbBaseName(clientUid: string): string {
  return 'userdb-coach_' + clientUid;
}

/**
 * The local store that holds a coach's own client roster (df-a5263ec09237).
 *
 * if an admin is impersonating the coach, the roster is in the impersonation copy, which is
 * always opened under the plain base name. Otherwise it is in the signed-in coach's own
 * database, which `DatabaseWrapper` opens under the reset-generation name — so this applies
 * the same mapping, or a reset device would read a superseded, empty store.
 */
export function coachOwnRosterDbName(
  storage: GenerationStorage | undefined | null,
  impersonatedUid: string | undefined,
  signedInUid: string,
): string {
  if (impersonatedUid) return localUserDbBaseName(impersonatedUid);
  return currentLocalStoreName(storage, localUserDbBaseName(signedInUid));
}
